import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { setVizLoading } from '../../../../store/visualizerSlice';
import ProductGroupStep from '../ProductGroupStep/ProductGroupStep';
import StepNavigation from '../../StepNavigation/StepNavigation';
import PatternTab from './PatternTab';
import styles from './BathFloor.module.css';

const TABS = ['Tile', 'Pattern', 'Grout'];
// 2026-08-14: Brick horizontal and vertical share base pattern 176471;
// horizontal is distinguished by rotation code 254254 in the sel string.
const DEFAULT_PATTERN_ID = 176471;
const DEFAULT_PATTERN_TYPE = 'brick-pattern-horizontal';
const DEFAULT_GROUT_ID = -1;
const FIXED_ARG3 = 147607;
// Rotation code — 254252 for standard/horizontal lays, 254254 for vertical lays.
// Horizontal brick is a special case: uses 254254 with base pattern 176471.
const ARG4_HORIZONTAL = 254252;
const ARG4_VERTICAL = 254254;
const ARG4_BRICK_HORIZONTAL = 254254;

function isVerticalPattern(pattern) {
    const type = String(pattern?.type || '').toLowerCase();
    const name = String(pattern?.name || '').toLowerCase();
    return type.includes('vertical') || name.includes('vertical');
}

function resolveArg4(patternType, vertical) {
    if (patternType === 'brick-pattern-horizontal') return ARG4_BRICK_HORIZONTAL;
    return vertical ? ARG4_VERTICAL : ARG4_HORIZONTAL;
}

function isStandardTier(tier) {
    return String(tier || '').trim().toLowerCase() === 'standard';
}

// Match bath floor surfaces but NOT shower floors.
function isBathFloorSurface(name) {
    const n = String(name || '').toLowerCase();
    if (!n.includes('floor')) return false;
    if (n.includes('shower')) return false;
    return true;
}

function applyStandard(tileId, dispatch) {
    const chamViz = document.getElementById('chamViz');
    if (!chamViz || !tileId) return;

    dispatch(setVizLoading(true));

    chamViz.applyByFilter(
        tileId,
        '',
        function(newSel) {
            if (isBathFloorSurface(this.name)) return newSel;
            return this.sel;
        },
        true
    );
}

function applyPatterned(patternId, patternType, tileId, groutId, surfaceName, dispatch, vertical = false) {
    const chamViz = document.getElementById('chamViz');
    if (!chamViz || !patternId) return;

    const arg4 = resolveArg4(patternType, vertical);
    const sel = `${patternId}(${tileId},${groutId},${FIXED_ARG3},${arg4})`;
    dispatch(setVizLoading(true));

    chamViz.applyByFilter(
        patternId,
        '',
        function() {
            if (isBathFloorSurface(this.name)) return sel;
            return this.sel;
        },
        true
    );
}

export default function BathFloor({
    surfaceName = 'Bath Floor',
    stepIndex,
    room = '',
    onViewAllApplied,
    onNext,
    onPrevious
}) {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('Tile');
    const [patternId, setPatternId] = useState(DEFAULT_PATTERN_ID);
    const [patternType, setPatternType] = useState(DEFAULT_PATTERN_TYPE);
    const [isVertical, setIsVertical] = useState(false);
    const [tileId, setTileId] = useState(null);
    const [groutId, setGroutId] = useState(DEFAULT_GROUT_ID);
    const [isStandard, setIsStandard] = useState(false);

    const handleTileSelect = useCallback((product, meta = {}) => {
        const standard = isStandardTier(meta.tier);
        setTileId(product.id);
        setIsStandard(standard);

        if (standard) {
            applyStandard(product.id, dispatch);
        } else {
            applyPatterned(patternId, patternType, product.id, groutId, surfaceName, dispatch, isVertical);
        }
    }, [patternId, patternType, groutId, surfaceName, dispatch, isVertical]);

    const handlePatternSelect = useCallback((pattern) => {
        if (isStandard) return;
        setPatternId(pattern.id);
        setPatternType(pattern.type);
        const vertical = isVerticalPattern(pattern);
        setIsVertical(vertical);
        if (!tileId) return;
        applyPatterned(pattern.id, pattern.type, tileId, groutId, surfaceName, dispatch, vertical);
    }, [isStandard, tileId, groutId, surfaceName, dispatch]);

    const handleGroutSelect = useCallback((product) => {
        if (isStandard) return;
        setGroutId(product.id);
        if (!tileId) return;
        applyPatterned(patternId, patternType, tileId, product.id, surfaceName, dispatch, isVertical);
    }, [isStandard, patternId, patternType, tileId, surfaceName, dispatch, isVertical]);

    const handleTabClick = (tab) => {
        if (isStandard && (tab === 'Pattern' || tab === 'Grout')) return;
        setActiveTab(tab);
    };

    return (
        <div className={styles.wrapper}>
            <StepNavigation
                title="Bathroom Flooring"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.tabs}>
                {TABS.map(tab => {
                    const locked = isStandard && (tab === 'Pattern' || tab === 'Grout');
                    return (
                        <button
                            key={tab}
                            className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''} ${locked ? styles.tabLocked : ''}`}
                            onClick={() => handleTabClick(tab)}
                            disabled={locked}
                            aria-disabled={locked}
                            title={locked ? 'Not applicable for standard products' : undefined}
                        >
                            {tab}
                        </button>
                    );
                })}
            </div>

            <div className={styles.tabContent}>
                {activeTab === 'Tile' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="BathFloor"
                            seriesId="9782"
                            surfaceName={surfaceName}
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            onProductSelect={handleTileSelect}
                        />
                    </div>
                )}

                {activeTab === 'Pattern' && !isStandard && (
                    <PatternTab
                        selectedPatternType={patternType}
                        onPatternSelect={handlePatternSelect}
                    />
                )}

                {activeTab === 'Grout' && !isStandard && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="BathFloorGrout"
                            seriesId="8311"
                            surfaceName={surfaceName}
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            noFilter
                            onProductSelect={handleGroutSelect}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

BathFloor.propTypes = {
    surfaceName: PropTypes.string,
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
